/**
 * useEventNotifications Hook
 *
 * Custom hook for managing event notifications with localStorage persistence.
 * Handles banner and modal state for multiple events.
 *
 * Features:
 * - LocalStorage persistence for dismissed events
 * - Auto-show modal on first visit
 * - Banner visibility management
 * - Future-ready for database integration
 */

import {
  getActiveBannerEvents,
  getActiveModalEvents,
} from "@/config/events.config";
import { Event, EventNotificationState } from "@/types/events.types";
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "nari_shakti_event_notifications";

/**
 * Get stored notification states from localStorage
 */
const getStoredNotificationStates = (): Record<
  string,
  EventNotificationState
> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error reading event notification states:", error);
    return {};
  }
};

/**
 * Save notification states to localStorage
 */
const saveNotificationStates = (
  states: Record<string, EventNotificationState>
) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(states));
  } catch (error) {
    console.error("Error saving event notification states:", error);
  }
};

/**
 * Get notification state for a specific event
 */
const getEventNotificationState = (eventId: string): EventNotificationState => {
  const states = getStoredNotificationStates();
  return (
    states[eventId] || {
      eventId,
      dismissed: false,
      modalShown: false,
    }
  );
};

/**
 * Update notification state for a specific event
 */
const updateEventNotificationState = (
  eventId: string,
  updates: Partial<EventNotificationState>
) => {
  const states = getStoredNotificationStates();
  states[eventId] = {
    ...getEventNotificationState(eventId),
    ...updates,
  };
  saveNotificationStates(states);
};

export const useEventNotifications = () => {
  const [activeBannerEvents, setActiveBannerEvents] = useState<Event[]>([]);
  const [activeModalEvent, setActiveModalEvent] = useState<Event | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Load active events and check their notification states
   */
  const loadActiveEvents = useCallback((showModalOnLoad = true) => {
    const bannerEvents = getActiveBannerEvents();
    const modalEvents = getActiveModalEvents();

    // Clear dismissal states on page refresh - show all banners again
    const states = getStoredNotificationStates();
    const clearedStates = { ...states };
    bannerEvents.forEach((event) => {
      if (clearedStates[event.id]) {
        clearedStates[event.id].dismissed = false;
      }
    });
    saveNotificationStates(clearedStates);

    // Show all active banner events (dismissal cleared)
    setActiveBannerEvents(bannerEvents);

    // Show modal on every login if there are modal events
    if (showModalOnLoad && modalEvents.length > 0) {
      const modalEventToShow = modalEvents[0]; // Show first active modal event
      setActiveModalEvent(modalEventToShow);
      setIsModalOpen(true);
    }
  }, []);
  /**
   * Dismiss a banner event (session only - returns on next login)
   */
  const dismissBanner = useCallback((eventId: string) => {
    setActiveBannerEvents((prev) =>
      prev.filter((event) => event.id !== eventId)
    );
    // Store dismissal in state but don't persist
    updateEventNotificationState(eventId, {
      dismissed: true,
      dismissedAt: new Date(),
    });
  }, []);

  /**
   * Open modal for a specific event
   */
  const openModal = useCallback((event: Event) => {
    setActiveModalEvent(event);
    setIsModalOpen(true);
  }, []);

  /**
   * Close modal
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  /**
   * Load events on mount
   */
  useEffect(() => {
    loadActiveEvents(true);
  }, [loadActiveEvents]);

  return {
    activeBannerEvents,
    activeModalEvent,
    isModalOpen,
    dismissBanner,
    openModal,
    closeModal,
    loadActiveEvents,
  };
};

export default useEventNotifications;
